import { Controller } from "@hotwired/stimulus";
import { Calendar } from "@fullcalendar/core";
import {
  locale
} from "../components/fullcalendar.js";
import { initializeRadioButtons } from "../components/square-radio-button";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline";
import interactionPlugin from "@fullcalendar/interaction";
import { t } from "../i18n/t";
import getCsrfToken from "../utils/csrf";
import $ from "jquery";

export default class extends Controller {
  connect() {
    this.schedulerMount();

    document.addEventListener(
      "turbolinks:before-cache",
      () => {
        this.schedulerUnmount();
      },
      { once: true }
    );

    // Bootstrap modal events are emitted via jQuery.
    this.modalElement = this.element.closest('.modal');
    this.boundModalShownHandler = () => this.ensureCalendarLayout();
    $(document).on('shown.bs.modal.bundleScheduler', '#modal__bundle_wizard', this.boundModalShownHandler);
    if (this.modalElement && this.modalElement.classList.contains('show')) {
      this.ensureCalendarLayout();
    }

    this.boundAvailabilityCheckedHandler = (event) => this.onAvailabilityChecked(event);
    window.addEventListener('bundles:availability-checked', this.boundAvailabilityCheckedHandler);

    this.boundTopDateRangeChangedHandler = (event) => this.onTopDateRangeChanged(event);
    window.addEventListener('bundles:top-date-range-changed', this.boundTopDateRangeChangedHandler);
  }

  disconnect() {
    if (this.boundModalShownHandler) {
      $(document).off('shown.bs.modal.bundleScheduler', '#modal__bundle_wizard', this.boundModalShownHandler);
    }

    if (this.boundAvailabilityCheckedHandler) {
      window.removeEventListener('bundles:availability-checked', this.boundAvailabilityCheckedHandler);
    }

    if (this.boundTopDateRangeChangedHandler) {
      window.removeEventListener('bundles:top-date-range-changed', this.boundTopDateRangeChangedHandler);
    }

    this.schedulerUnmount();
  }

  ensureCalendarLayout() {
    if (!this.calendar) return;

    requestAnimationFrame(() => {
      this.calendar.updateSize();
    });
  }

  schedulerMount() {
    const resourcesUrl = this.data.get("resources-url");
    const eventsUrl = this.data.get("events-url");
    const dateType = this.element.dataset.bundleSchedulerDateType;
    const calendarLocale = locale();

    const resourceAreaColumns = [
      {
        headerContent: t("bundles.listings.title", calendarLocale),
        cellContent: (arg) => {
          if (arg.resource.extendedProps && arg.resource.extendedProps.isGroup) return;

          const listingId = String(arg.resource.id).trim();
          const template = document.getElementById(`template-listing-${listingId}`);

          if (template) {
            const container = document.createElement('div');
            container.appendChild(template.content.cloneNode(true));
            return { domNodes: [container] };
          }
          return { html: `<span class="p-2 font-bold">${arg.resource.title}</span>` };
        }
      }
    ];

      resourceAreaColumns.push({
        headerContent: t("bundles.dates", calendarLocale),
        width: dateType === "fixed" ? "0px" : "auto",
        cellContent: (arg) => {
          if (arg.resource.extendedProps && arg.resource.extendedProps.isGroup) return;

          const listingId = String(arg.resource.id).trim();
          const template = document.getElementById(`template-dates-${listingId}`);

          if (template) {
            const container = document.createElement('div');
            container.appendChild(template.content.cloneNode(true));
            return { domNodes: [container] };
          }
          return { html: `<span></span>` };
        }
      });

    const calendar = new Calendar(this.element, {
      plugins: [resourceTimelinePlugin, interactionPlugin],
      timeZone: "UTC",
      initialView: "resourceTimelineWeeks",
      resourceAreaWidth: dateType === "fixed" ? "30%" : "50%",
      firstDay: 1,
      height: "auto",
      locale: calendarLocale.slice(0, 2),

      eventContent: ({ event }, createElement) => {
        if (event.classNames.includes("availability--bundle-available")) {
          return createElement("div", { class: "calendar-event calendar-event--bundle-available" }, [
            createElement("span", { class: "bundle-available-line" }, null),
            createElement(
              "div",
              { class: "calendar-event-title uppercase text-gray-600 font-bold text-[10px] text-center",
                title: event.title }, 
              t("bundles.available", calendarLocale)
          )
          ]);
        }

        let calendarEventContent = [
          createElement(
              "div",
              { class: "calendar-event-title uppercase text-gray-600 font-bold text-[10px] text-center",
                title: event.title }, 
              t("bundles.not_available", calendarLocale)
          ),
        ];

        return createElement("div", { class: "calendar-event relative" }, calendarEventContent);
      },

      views: {
        resourceTimelineWeeks: {
          type: "resourceTimeline",
          duration: { days: 15 },
          buttonText: t("scheduler.weeks", calendarLocale),
          slotLabelFormat: [{ week: "short" }, { day: "numeric" }],
        },
      },

      resourceAreaColumns: resourceAreaColumns,
      resources: resourcesUrl,
      resourceOrder: "index",
      events: (info, successCallback) => {
        fetch(`${eventsUrl}&start=${info.startStr}&end=${info.endStr}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": getCsrfToken(),
          },
        })
            .then((response) => {
              if (!response.ok) {
                throw new Error(`Bundle events request failed with status ${response.status}`);
              }

              return response.json();
            })
            .then((data) => {
              successCallback(Array.isArray(data) ? data : []);
            })
            .catch((error) => {
              console.error("Failed to load bundle events:", error);
              const fallbackEvents = (this.calendar?.getResources?.() || [])
                .filter((resource) => !(resource.extendedProps && resource.extendedProps.isGroup))
                .map((resource) => ({
                  id: `bundle-fallback-${String(resource.id).trim()}-${info.startStr}-${info.endStr}`,
                  resourceId: String(resource.id),
                  start: info.startStr,
                  end: info.endStr,
                  title: t("bundles.not_available", calendarLocale),
                  classNames: ["availability--bundle-unavailable"],
                }));

              successCallback(fallbackEvents);
            });
      },
      slotDuration: "24:00:00",

      // License
      schedulerLicenseKey: "0903568687-fcs-1610921093",
    });

    calendar.render();
    initializeRadioButtons();
    this.calendar = calendar;

    requestAnimationFrame(() => {
      this.calendar.updateSize();
    });
  }

  schedulerUnmount() {
    if (this.calendar) {
      this.calendar.destroy();
      this.calendar = null;
    }
  }

  onAvailabilityChecked(event) {
    if (!this.calendar || !event?.detail) return;

    const { bookingIndex, listingId, startDate, endDate, available, price } = event.detail;
    if (bookingIndex === undefined || !listingId) return;

    const calendarLocale = locale();
    const dateFormat = this.dateRangeFormatForRow(bookingIndex, listingId);
    const normalizedStartDate = this.normalizeDateForCalendar(startDate, dateFormat);
    const endDateWithExtraDay = this.finalEndDay(endDate, dateFormat);
    const normalizedEndDate = this.normalizeDateForCalendar(endDateWithExtraDay, dateFormat);
    const calendarResourceId = this.resolveCalendarResourceId(listingId);
    if (!calendarResourceId) return;

    const previewEventId = `bundle-check-${bookingIndex}`;
    const liveSelectionEventId = `live-selection-${bookingIndex}`;
    this.calendar.getEventById(previewEventId)?.remove();
    this.calendar.getEventById(liveSelectionEventId)?.remove();

    if (!normalizedStartDate || !normalizedEndDate) {
      this.ensureCalendarLayout();
      return;
    }

    this.calendar.gotoDate(normalizedStartDate);

    const isAvailable = Boolean(available) && Number(price || 0) > 0;

    this.calendar.addEvent({
      id: previewEventId,
      resourceId: calendarResourceId,
      start: normalizedStartDate,
      end: normalizedEndDate,
      title: isAvailable ? t("bundles.available", calendarLocale) : t("bundles.not_available", calendarLocale),
      display: 'auto',
      backgroundColor: isAvailable ? '#e9f8f3' : '#f6e9ec',
      borderColor: isAvailable ? '#10b981' : '#ef4444',
      classNames: [
        'bundle-selection-event',
        isAvailable ? 'bundle-selection-event--available' : 'bundle-selection-event--unavailable',
        isAvailable ? 'availability--bundle-available' : 'availability--bundle-unavailable'
      ],
      extendedProps: { isBundleAvailabilityPreview: true }
    });

    this.ensureCalendarLayout();
  }

  onTopDateRangeChanged(event) {
    if (!this.calendar || !event?.detail) return;

    const { fromDate, toDate, dateFormat } = event.detail;
    const targetDate = fromDate || toDate;
    if (!targetDate) return;

    const normalizedTargetDate = this.normalizeDateForCalendar(targetDate, dateFormat);
    if (!normalizedTargetDate) return;

    this.calendar.gotoDate(normalizedTargetDate);
    this.ensureCalendarLayout();
  }

  dateRangeFormatForRow(bookingIndex, listingId) {
    const row = this.element.querySelector(`.selected_listings_date[data-booking-index="${bookingIndex}"][data-listing-id="${listingId}"]`) ||
      this.element.querySelector(`.selected_listings_date[data-booking-index="${bookingIndex}"]`) ||
      this.element.querySelector(`.selected_listings_date[data-listing-id="${listingId}"]`);

    return row?.dataset?.dateRangeDateFormat || null;
  }

  normalizeDateForCalendar(rawDate, format) {
    if (!rawDate) return null;
    if (rawDate instanceof Date && !Number.isNaN(rawDate.getTime())) {
      return this.toISODate(rawDate);
    }

    if (typeof rawDate !== 'string') return null;
    const input = rawDate.trim();
    if (!input) return null;

    const parsedByFormat = this.parseDateByFormat(input, format);
    if (parsedByFormat) {
      return this.toISODate(parsedByFormat);
    }

    const parsedNative = new Date(input);
    if (!Number.isNaN(parsedNative.getTime())) {
      return this.toISODate(parsedNative);
    }

    return null;
  }

  toISODate(date) {
    const localDate = new Date(date);
    localDate.setMinutes(localDate.getMinutes() - localDate.getTimezoneOffset());
    return localDate.toISOString().split('T')[0];
  }

  finalEndDay(dateInput, format) {
    const parsed = this.parseDateByFormat(dateInput, format) || new Date(dateInput);
    if (Number.isNaN(parsed.getTime())) return dateInput;
    
    parsed.setDate(parsed.getDate() + 1);
    
    return this.toISODate(parsed);
  }

  parseDateByFormat(input, format) {
    if (!format) return null;

    const tokens = [];
    const escapedFormat = format.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regexSource = escapedFormat.replace(/[dmY]/g, (token) => {
      tokens.push(token);
      if (token === 'Y') return '(\\d{4})';
      return '(\\d{1,2})';
    });

    const match = input.match(new RegExp(`^${regexSource}$`));
    if (!match) return null;

    let day = null;
    let month = null;
    let year = null;

    tokens.forEach((token, idx) => {
      const value = Number(match[idx + 1]);
      if (token === 'd') day = value;
      if (token === 'm') month = value;
      if (token === 'Y') year = value;
    });

    if (!day || !month || !year) return null;

    const parsed = new Date(year, month - 1, day);
    if (parsed.getFullYear() !== year || parsed.getMonth() !== month - 1 || parsed.getDate() !== day) {
      return null;
    }

    return parsed;
  }

  resolveCalendarResourceId(listingId) {
    if (!this.calendar || listingId === undefined || listingId === null) return null;

    const id = String(listingId);
    if (this.calendar.getResourceById(id)) {
      return id;
    }

    const trimmed = id.trim();
    const resources = this.calendar.getResources?.() || [];
    const matched = resources.find((resource) => String(resource.id).trim() === trimmed);
    return matched ? String(matched.id) : null;
  }
}
